import { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'next-i18next';
import Container from './Container';
import { sendEvent } from '../utils/analytics';
import IconArrowDown from '../public/images/arrow-down-mini.svg';

const Faq = ({ items, ...props }) => {
  const { t } = useTranslation('common');

  return (
    <div
      className="faq"
      itemScope
      itemType="https://schema.org/FAQPage"
      {...props}
    >
      <Container>
        <h2 className="faq__title">
          {t('faq.title')}
        </h2>
        <ul className="faq__items" role="none">
          {items.map(({ question, answer }, index) => (
            <FaqItem
              key={question}
              id={`faq-item-${index + 1}`}
              question={question}
              answer={answer}
            />
          ))}
        </ul>
      </Container>
    </div>
  );
};

const FaqItem = ({ id, question, answer }) => {
  const [open, setOpen] = useState(false);

  const handleClick = useCallback(() => {
    setOpen((open) => {
      open = !open;

      if (open) {
        sendEvent({
          category: 'faq',
          action: 'item-open',
          label: id,
        });
      }

      return open;
    });
  }, [id]);

  const handleKeyPress = useCallback((event) => {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      handleClick();
    }
  }, [handleClick]);

  return (
    <li
      key={question}
      id={id}
      className="faq__item"
      itemScope
      itemType="https://schema.org/Question"
      itemProp="mainEntity"
      role="region"
      aria-label={question}
    >
      <h4
        className="faq__question"
        itemProp="name"
        role="button"
        aria-controls={`${id}-answer`}
        aria-expanded={String(open)}
        onClick={handleClick}
        onKeyPress={handleKeyPress}
        tabIndex="0"
      >
        {question}
        <IconArrowDown aria-hidden="true" />
      </h4>
      <div
        id={`${id}-answer`}
        className="faq__answer content"
        itemScope
        itemType="https://schema.org/Answer"
        itemProp="acceptedAnswer"
      >
        <div itemProp="text">
          {answer.map((answerItem, i) => (
            Array.isArray(answerItem) ? (
              <ul key={`${question}:${i}`}>
                {answerItem.map((item, j) => (
                  Array.isArray(item) ? (
                    <li key={`${question}:${i}:${j}`}>
                      {item.map((item, k) => (
                        <p
                          key={`${question}:${i}:${j}:${k}`}
                          dangerouslySetInnerHTML={{ __html: item }}
                        />
                      ))}
                    </li>
                  ) : (
                    <li
                      key={`${question}:${i}:${j}`}
                      dangerouslySetInnerHTML={{ __html: item }}
                    />
                  )
                ))}
              </ul>
            ) : (
              <p
                key={`${question}:${i}`}
                dangerouslySetInnerHTML={{ __html: answerItem }}
              />
            )
          ))}
        </div>
      </div>
    </li>
  );
};

Faq.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({
    question: PropTypes.string.isRequired,
    answer: PropTypes.arrayOf(PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.string),
      ])),
    ])).isRequired,
  })).isRequired,
};

FaqItem.propTypes = {
  id: PropTypes.string.isRequired,
  question: PropTypes.string.isRequired,
  answer: PropTypes.arrayOf(PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string),
    ])),
  ])).isRequired,
};

export default Faq;
